import { useSearchParams } from 'react-router';
import { useUrlSearch } from './useUrlSearch';
import type { SortDirection } from '../components/DataTable';

export interface TransactionFilterValues {
  q: string;
  statuses: string[];
  categories: string[];
  type: string;
  program_uid: string;
  from: string;
  to: string;
}

const ARRAY_FILTERS = ['statuses', 'categories'];

export const useTransactionFilters = () => {
  const { query, page, search, setSearch, goToPage } = useUrlSearch();
  const [searchParams, setSearchParams] = useSearchParams();

  const filters: TransactionFilterValues = {
    q: search,
    statuses: searchParams.getAll('statuses'),
    categories: searchParams.getAll('categories'),
    type: searchParams.get('type') ?? '',
    program_uid: searchParams.get('program_uid') ?? '',
    from: searchParams.get('from') ?? '',
    to: searchParams.get('to') ?? '',
  };

  // The API only accepts starts_at and ends_at together, so a half-filled range is not applied.
  const hasDateRange = filters.from !== '' && filters.to !== '' && filters.to >= filters.from;

  const hasFilters =
    filters.q !== '' ||
    filters.statuses.length > 0 ||
    filters.categories.length > 0 ||
    filters.type !== '' ||
    filters.program_uid !== '' ||
    hasDateRange;

  const canClear = hasFilters || filters.from !== '' || filters.to !== '';

  const setFilter = (name: keyof TransactionFilterValues, value: string | string[]) => {
    if (name === 'q') {
      setSearch(value as string);
      return;
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(name);
        if (ARRAY_FILTERS.includes(name)) (value as string[]).forEach((v) => next.append(name, v));
        else if (value) next.set(name, value as string);
        next.delete('page');
        return next;
      },
      { replace: true },
    );
  };

  const sortDirection: SortDirection = searchParams.get('sort') === 'ASC' ? 'ASC' : 'DESC';

  const toggleSort = () => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (sortDirection === 'DESC') next.set('sort', 'ASC');
        else next.delete('sort');
        next.delete('page');
        return next;
      },
      { replace: true },
    );
  };

  const clearFilters = () => {
    setSearch('');
    const next = new URLSearchParams();
    if (sortDirection === 'ASC') next.set('sort', 'ASC');
    setSearchParams(next, { replace: true });
  };

  return {
    filters,
    query,
    page,
    goToPage,
    hasFilters,
    hasDateRange,
    canClear,
    setFilter,
    clearFilters,
    sortDirection,
    toggleSort,
    searchParams,
  };
};
